import { FixedTimeRange, Timestamp } from "@/types/date"
import { InspirationRef } from "@/types/entity"
import { InspirationSummary } from "@/types/inspiration"
import {
  CrawledUrlSource,
  MESSAGE_COUNT_FILTER_MAX,
  OrganizationAdmin,
  OrgCrawledUrl,
  OrgMicrosite,
  PlacesStatusReport,
} from "@/types/organization"
import { PerOrganizationPermission } from "@/types/user"
import { VanityReports } from "@/types/vanity"
import { QueryStatus, skipToken } from "@reduxjs/toolkit/query/react"

import {
  CreateKeyFactData,
  KeyFact,
  UpdateKeyFactData,
} from "@/lib/key-facts-api"
import { CtaInsight, OrgFeedbackData } from "@/lib/organization-api"
import { PagingMeta } from "@/lib/paging"
import { inspirationApi } from "@/lib/storage/api/inspiration-api"
import { AppDispatch, RootState } from "@/lib/storage/redux-store"
import type { OrgConfigValues } from "@/components/organization/config-form"
import type { OrgCrawlStats } from "@/components/organization/crawls"
import type {
  CopyCreatedRange,
  InspirationTagColor,
  InspirationTagDefinition,
  OrgInspiration,
  OrgInspirationCounts,
  OrgInspirationScope,
  OrgInspirationStatus,
} from "@/components/organization/embed-snippets"
import type { FeaturedOnlyOption } from "@/components/organization/featured-only-picker"
import type {
  ChatInsightsTrendsEntry,
  OrgChatData,
  OrgChatFilterOptions,
  OrgMessageData,
  OrgMessagesSummary,
  OrgMessageType,
} from "@/components/organization/message-table"
import type { MessageTypeOption } from "@/components/organization/message-type-picker"
import type { OrgPlaceData } from "@/components/organization/place-table"
import type { PlaceTypePickerOption } from "@/components/organization/place-type-picker"
import type { OrgMember } from "@/components/organization/team-roles"
import type { OrgUserData } from "@/components/organization/user-table"
import type { EntityPickerOption } from "@/components/organization/user-type-picker"
import { baseApi } from "./base-api"

export { skipToken }

export interface OrgTimeRangeArgs {
  startDate: number
  endDate: number
}

export function toOrgTimeRangeArgs(
  timeRange: FixedTimeRange
): OrgTimeRangeArgs {
  return {
    startDate: timeRange.from.epoch_millis,
    endDate: timeRange.to.epoch_millis,
  }
}

function orgTimeRangeParams(range: OrgTimeRangeArgs) {
  return {
    start_date: range.startDate.toString(),
    end_date: range.endDate.toString(),
  }
}

// the Chats-tab filters shared by the chat list and its filter-option counts
export interface OrgChatFilters {
  text?: string
  userSatFilter?: [number, number]
  messageCountFilter?: [number, number]
  intentFilter?: string
  messageTypeFilter?: MessageTypeOption
  originFilter?: string
  languageFilter?: string
}

function orgChatFilterParams(filters: OrgChatFilters) {
  const params: Record<string, string> = {}
  if (filters.text) params.text = filters.text
  if (filters.userSatFilter) {
    const [min, max] = filters.userSatFilter
    const expandedScores = Array.from(
      { length: max - min + 1 },
      (_, i) => min + i
    )
    params.csat_scores = expandedScores.join(",")
  }
  if (filters.messageCountFilter) {
    const [min, max] = filters.messageCountFilter
    params.message_count_min = min.toString()
    if (max < MESSAGE_COUNT_FILTER_MAX) {
      params.message_count_max = max.toString()
    }
  }
  if (filters.intentFilter) params.intent = filters.intentFilter
  if (filters.messageTypeFilter) params.message_type = filters.messageTypeFilter
  if (filters.originFilter) params.origin = filters.originFilter
  if (filters.languageFilter) params.language = filters.languageFilter
  return params
}

export interface GetOrgChatsArgs extends OrgChatFilters {
  orgid: string
  timeRange: OrgTimeRangeArgs
  page: number
  perPage: number
  messageType?: OrgMessageType
  chatId?: number
}

export interface GetOrgPlacesArgs {
  orgid: string
  timeRange: OrgTimeRangeArgs
  page: number
  perPage: number
  placeType: PlaceTypePickerOption
  featuredOnly: FeaturedOnlyOption
  searchFilter: string
  sortBy?: string
  sortOrder?: string
}

export interface GetOrgUsersArgs {
  orgid: string
  timeRange: OrgTimeRangeArgs
  userType: EntityPickerOption
  page: number
  perPage: number
  searchFilter?: string
}

export interface GetOrgFeedbacksArgs {
  orgid: string
  startDate?: string
  endDate?: string
  statuses?: string[]
  page: number
  perPage: number
  feedbackType?: "thumbs_up" | "thumbs_down"
  commentFilter?: "with_comment" | "all"
  chatId?: number
  textSearch?: string
}

export interface GetOrgCrawledUrlsArgs {
  orgid: string
  timeRange?: OrgTimeRangeArgs
  filter: string
  onlyActive: boolean
  closedOnly?: boolean
  page: number
  perPage: number
  kind: CrawledUrlSource
}

export interface GetOrgChatMessagesArgs {
  orgid: string
  chatId: number
  page: number
  perPage: number
  includeInitial?: boolean
}

export interface GetOrgKeyFactsArgs {
  orgid: string
  page: number
  perPage: number
  textSearch?: string
}

export interface GetUserCountriesArgs {
  orgid: string
  startDate: string
  endDate: string
}

export interface GetOrgInspirationsArgs {
  orgid: string
  page: number
  perPage: number
  tagIds: string[]
  search?: string
  createdBy?: string[]
  lastEditedBy?: string[]
  sortBy?: "created_at"
  sortOrder?: "asc" | "desc"
  scope?: OrgInspirationScope
  status?: OrgInspirationStatus
  createdWithinDays?: CopyCreatedRange
}

export const organizationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganization: builder.query<OrganizationAdmin, string>({
      query: (orgid) => ({
        url: `/api/organizations/${orgid}`,
        skipAuthHandling: true,
      }),
      providesTags: (_result, _error, orgid) => [
        { type: "Organization", id: orgid },
      ],
    }),

    updateOrganizationConfig: builder.mutation<
      OrganizationAdmin,
      { orgid: string; config: Partial<OrgConfigValues> }
    >({
      query: ({ orgid, config }) => ({
        url: `/api/organizations/${orgid}`,
        method: "PUT",
        data: config,
      }),
      async onQueryStarted({ orgid }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled
          const cached =
            organizationApi.endpoints.getOrganization.select(orgid)(
              getState()
            ).data
          void dispatch(
            organizationApi.util.upsertQueryData("getOrganization", orgid, {
              ...cached,
              ...data,
              time_zone: data.time_zone ?? cached?.time_zone,
              locations: data.locations ?? cached?.locations,
            })
          )
        } catch {
          // mutation failed — cache unchanged
        }
      },
    }),

    getMicrosite: builder.query<OrgMicrosite, string>({
      query: (orgid) => ({ url: `/api/organizations/${orgid}/microsite` }),
    }),

    setupMicrosite: builder.mutation<OrgMicrosite, string>({
      query: (orgid) => ({
        url: `/api/organizations/${orgid}/microsite`,
        method: "POST",
      }),
      async onQueryStarted(orgid, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          void dispatch(
            organizationApi.util.upsertQueryData("getMicrosite", orgid, data)
          )
        } catch {
          // mutation failed — cache unchanged
        }
      },
      // setup saves the org, so refetch for a fresh lock_version
      invalidatesTags: (_result, _error, orgid) => [
        { type: "Organization", id: orgid },
      ],
    }),

    getAvailableFeatures: builder.query<string[], void>({
      query: () => ({
        url: `/api/organizations/available_features`,
      }),
      keepUnusedDataFor: 3600, // static reference data, safe to cache
    }),

    getPermissionsForOrg: builder.query<PerOrganizationPermission[], string>({
      query: (orgid) => ({
        url: `/api/organizations/${orgid}/permissions`,
      }),
      transformResponse: (response: {
        permissions: PerOrganizationPermission[]
      }) => response.permissions,
      providesTags: (_result, _error, orgid) => [
        { type: "OrgPermissions", id: orgid },
      ],
    }),

    getUserCountries: builder.query<
      { countries: { name: string; users: number }[] },
      GetUserCountriesArgs
    >({
      query: ({ orgid, startDate, endDate }) => ({
        url: `/api/organizations/${orgid}/user_countries`,
        params: { start_date: startDate, end_date: endDate },
      }),
    }),

    getOrgChats: builder.query<
      { data: OrgChatData[]; meta: PagingMeta },
      GetOrgChatsArgs
    >({
      query: ({
        orgid,
        timeRange,
        page,
        perPage,
        messageType,
        chatId,
        ...filters
      }) => {
        const params: Record<string, string> = {
          ...orgTimeRangeParams(timeRange),
          page: page.toString(),
          per_page: perPage.toString(),
        }
        if (messageType) params.message_type = messageType
        if (chatId) params.chat_id = chatId.toString()
        Object.assign(params, orgChatFilterParams(filters))
        return {
          url: `/api/organizations/${orgid}/chats`,
          params,
        }
      },
      providesTags: (_result, _error, { orgid }) => [
        { type: "OrgChats", id: orgid },
      ],
    }),

    // includeMindtrip is false for Insights dialog, omitted to use AIS config
    getOrgChatInsights: builder.query<
      OrgMessagesSummary,
      { orgid: string; includeMindtrip?: boolean }
    >({
      query: ({ orgid, includeMindtrip }) => ({
        url: `/api/organizations/${orgid}/chat_insights`,
        params:
          includeMindtrip === undefined
            ? undefined
            : { include_mindtrip: includeMindtrip },
      }),
      providesTags: (_result, _error, { orgid }) => [
        { type: "OrgChatInsights", id: orgid },
      ],
    }),

    getOrgChatInsightsTrends: builder.query<ChatInsightsTrendsEntry[], string>({
      query: (orgid) => ({
        url: `/api/organizations/${orgid}/chat_insights_trends`,
      }),
      transformResponse: (response: { trends: ChatInsightsTrendsEntry[] }) =>
        response.trends,
      providesTags: (_result, _error, orgid) => [
        { type: "OrgChatInsights", id: orgid },
      ],
    }),

    getOrgCtaInsights: builder.query<CtaInsight[], string>({
      query: (orgid) => ({
        url: `/api/organizations/${orgid}/cta_insights`,
        params: { limit: "50" },
      }),
    }),

    getOrgChatFilterOptions: builder.query<
      OrgChatFilterOptions,
      OrgChatFilters & { orgid: string; timeRange: OrgTimeRangeArgs }
    >({
      query: ({ orgid, timeRange, ...filters }) => ({
        url: `/api/organizations/${orgid}/chat_filter_options`,
        params: {
          ...orgTimeRangeParams(timeRange),
          ...orgChatFilterParams(filters),
        },
      }),
    }),

    getOrgPlaces: builder.query<
      {
        data: OrgPlaceData[]
        meta: PagingMeta & {
          mention_count: number
          featured_count: number
          handoff_count: number
          view_count: number
        }
      },
      GetOrgPlacesArgs
    >({
      query: ({
        orgid,
        timeRange,
        page,
        perPage,
        placeType,
        featuredOnly,
        searchFilter,
        sortBy,
        sortOrder,
      }) => {
        const params: Record<string, string> = {
          ...orgTimeRangeParams(timeRange),
          page: page.toString(),
          per_page: perPage.toString(),
          place_type: placeType,
          featured_only: featuredOnly,
        }
        if (searchFilter) params.filter = searchFilter
        if (sortBy) params.sort_by = sortBy
        if (sortOrder) params.sort_order = sortOrder
        return {
          url: `/api/organizations/${orgid}/places`,
          params,
        }
      },
      providesTags: (_result, _error, { orgid }) => [
        { type: "OrgPlaces", id: orgid },
      ],
    }),

    updateFeaturedPlace: builder.mutation<
      { entity_ref: string; feature_level: number },
      { orgid: string; entityRef: string; featureLevel: number }
    >({
      query: ({ orgid, entityRef, featureLevel }) => ({
        url: `/api/organizations/${orgid}/places/${entityRef}`,
        method: "PUT",
        data: { feature_level: featureLevel },
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "OrgPlaces", id: orgid },
      ],
    }),

    removeFeaturedPlace: builder.mutation<
      { entity_ref: string; removed: boolean },
      { orgid: string; entityRef: string }
    >({
      query: ({ orgid, entityRef }) => ({
        url: `/api/organizations/${orgid}/places/${entityRef}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "OrgPlaces", id: orgid },
      ],
    }),

    getOrgPlacesStatus: builder.query<PlacesStatusReport, string>({
      query: (orgid) => ({
        url: `/api/organizations/${orgid}/places_status`,
      }),
    }),

    getOrgUsers: builder.query<
      { data: OrgUserData[]; meta: PagingMeta },
      GetOrgUsersArgs
    >({
      query: ({ orgid, timeRange, userType, page, perPage, searchFilter }) => {
        const params: Record<string, string> = {
          ...orgTimeRangeParams(timeRange),
          user_type: userType,
          page: page.toString(),
          per_page: perPage.toString(),
        }
        if (searchFilter) params.filter = searchFilter
        return {
          url: `/api/organizations/${orgid}/users`,
          params,
        }
      },
      providesTags: (_result, _error, { orgid }) => [
        { type: "OrgUsers", id: orgid },
      ],
    }),

    getOrgFeedbacks: builder.query<
      { data: OrgFeedbackData[]; meta: PagingMeta },
      GetOrgFeedbacksArgs
    >({
      query: ({
        orgid,
        startDate,
        endDate,
        statuses,
        page,
        perPage,
        feedbackType,
        commentFilter,
        chatId,
        textSearch,
      }) => {
        const params: Record<string, string> = {
          page: page.toString(),
          per_page: perPage.toString(),
        }
        if (startDate) params.start_date = startDate
        if (endDate) params.end_date = endDate
        if (statuses && statuses.length > 0) {
          params.statuses = statuses.join(",")
        }
        if (feedbackType) params.feedback_type = feedbackType
        if (commentFilter === "all") params.comment_filter = "all"
        if (chatId) params.chat_id = chatId.toString()
        if (textSearch) params.text = textSearch
        return {
          url: `/api/organizations/${orgid}/feedbacks`,
          params,
        }
      },
      providesTags: (_result, _error, { orgid }) => [
        { type: "OrgFeedbacks", id: orgid },
      ],
    }),

    updateOrgFeedback: builder.mutation<
      void,
      {
        orgid: string
        feedbackId: number
        data: { status?: string; comment?: string }
        skipInvalidation?: boolean
      }
    >({
      query: ({ orgid, feedbackId, data }) => ({
        url: `/api/organizations/${orgid}/feedbacks/${feedbackId}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: (_result, _error, { orgid, skipInvalidation }) =>
        skipInvalidation ? [] : [{ type: "OrgFeedbacks", id: orgid }],
    }),

    analyzeOrgFeedback: builder.mutation<
      { analysis: string },
      { orgid: string; feedbackId: number; force?: boolean }
    >({
      query: ({ orgid, feedbackId, force }) => ({
        url: `/api/organizations/${orgid}/feedbacks/${feedbackId}/analyze`,
        method: "POST",
        data: force ? { force: "true" } : {},
      }),
    }),

    getOrgChatMessages: builder.query<
      {
        data: OrgMessageData[]
        meta: PagingMeta & { chat_deactivated_at?: Timestamp | null }
      },
      GetOrgChatMessagesArgs
    >({
      query: ({ orgid, chatId, page, perPage, includeInitial }) => {
        const params: Record<string, string> = {
          chat_id: chatId.toString(),
          page: page.toString(),
          per_page: perPage.toString(),
        }
        if (includeInitial) params.include_initial = "true"
        return {
          url: `/api/organizations/${orgid}/chat_messages`,
          params,
        }
      },
    }),

    getOrgCrawls: builder.query<OrgCrawlStats, string>({
      query: (orgid) => ({
        url: `/api/organizations/${orgid}/crawls`,
      }),
      providesTags: (_result, _error, orgid) => [
        { type: "OrgCrawls", id: orgid },
      ],
    }),

    triggerOrgCrawl: builder.mutation<OrgCrawlStats, string>({
      query: (orgid) => ({
        url: `/api/organizations/${orgid}/crawls`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, orgid) => [
        { type: "OrgCrawls", id: orgid },
      ],
    }),

    getOrgCrawledUrls: builder.query<
      { data: OrgCrawledUrl[]; meta: PagingMeta },
      GetOrgCrawledUrlsArgs
    >({
      query: ({
        orgid,
        timeRange,
        filter,
        onlyActive,
        closedOnly,
        page,
        perPage,
        kind,
      }) => {
        const params: Record<string, string> = {
          only_active: onlyActive.toString(),
          page: page.toString(),
          per_page: perPage.toString(),
          kind,
        }
        if (timeRange) {
          Object.assign(params, orgTimeRangeParams(timeRange))
        }
        if (filter) params.filter = filter
        if (closedOnly) params.closed_only = "true"
        return {
          url: `/api/organizations/${orgid}/crawled_urls`,
          params,
        }
      },
      providesTags: (_result, _error, { orgid }) => [
        { type: "OrgCrawledUrls", id: orgid },
      ],
    }),

    getOrgMembers: builder.query<OrgMember[], string>({
      query: (orgid) => ({
        url: `/api/organizations/${orgid}/team`,
      }),
      providesTags: (_result, _error, orgid) => [
        { type: "OrgMembers", id: orgid },
      ],
    }),

    sendOrgInvitation: builder.mutation<
      OrgMember[],
      { orgid: string; email: string; role: string }
    >({
      query: ({ orgid, email, role }) => ({
        url: `/api/organizations/${orgid}/invite`,
        method: "POST",
        data: { email, role },
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "OrgMembers", id: orgid },
      ],
    }),

    changeOrgMemberRoles: builder.mutation<
      OrgMember[],
      { orgid: string; userid: number; roles: string[] }
    >({
      query: ({ orgid, userid, roles }) => ({
        url: `/api/organizations/${orgid}/team/${userid}`,
        method: "PUT",
        data: { roles },
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "OrgMembers", id: orgid },
      ],
    }),

    removeOrgMember: builder.mutation<
      OrgMember[],
      { orgid: string; userid: number }
    >({
      query: ({ orgid, userid }) => ({
        url: `/api/organizations/${orgid}/team/${userid}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "OrgMembers", id: orgid },
      ],
    }),

    getOrgKeyFacts: builder.query<
      {
        data: KeyFact[]
        meta: PagingMeta
        active_count: number
        on_demand_count: number
      },
      GetOrgKeyFactsArgs
    >({
      query: ({ orgid, page, perPage, textSearch }) => {
        const params: Record<string, string> = {
          page: page.toString(),
          per_page: perPage.toString(),
        }
        if (textSearch) params.text = textSearch
        return {
          url: `/api/organizations/${orgid}/key_facts`,
          params,
        }
      },
      providesTags: (_result, _error, { orgid }) => [
        { type: "OrgKeyFacts", id: orgid },
      ],
    }),

    createOrgKeyFact: builder.mutation<
      KeyFact,
      { orgid: string; data: CreateKeyFactData }
    >({
      query: ({ orgid, data }) => ({
        url: `/api/organizations/${orgid}/key_facts`,
        method: "POST",
        data,
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "OrgKeyFacts", id: orgid },
      ],
    }),

    updateOrgKeyFact: builder.mutation<
      KeyFact,
      { orgid: string; id: number; data: UpdateKeyFactData }
    >({
      query: ({ orgid, id, data }) => ({
        url: `/api/organizations/${orgid}/key_facts/${id}`,
        method: "PUT",
        data,
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "OrgKeyFacts", id: orgid },
      ],
    }),

    deleteOrgKeyFact: builder.mutation<
      { message: string },
      { orgid: string; id: number }
    >({
      query: ({ orgid, id }) => ({
        url: `/api/organizations/${orgid}/key_facts/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "OrgKeyFacts", id: orgid },
      ],
    }),

    getOrgInspirations: builder.query<
      {
        data: OrgInspiration[]
        counts?: OrgInspirationCounts
        tag_definitions: InspirationTagDefinition[]
        filter_options?: { created_by: string[]; last_edited_by: string[] }
        meta: PagingMeta
      },
      GetOrgInspirationsArgs
    >({
      query: ({
        orgid,
        page,
        perPage,
        tagIds,
        search,
        createdBy,
        lastEditedBy,
        sortBy,
        sortOrder,
        scope,
        status,
        createdWithinDays,
      }) => {
        const params: Record<string, string> = {
          page: page.toString(),
          per_page: perPage.toString(),
        }
        if (tagIds.length > 0) params.tags = tagIds.join(",")
        if (search) params.search = search
        if (createdBy?.length) params.created_by = createdBy.join(",")
        if (lastEditedBy?.length) params.last_edited_by = lastEditedBy.join(",")
        if (sortBy && sortOrder) {
          params.sort_by = sortBy
          params.sort_order = sortOrder
        }
        if (scope) params.scope = scope
        if (status) params.status = status
        if (createdWithinDays)
          params.created_within_days = createdWithinDays.toString()
        return {
          url: `/api/organizations/${orgid}/inspirations`,
          params,
        }
      },
      providesTags: (_result, _error, { orgid }) => [
        { type: "OrgInspirations", id: orgid },
      ],
    }),

    createCustomerCopy: builder.mutation<
      InspirationSummary,
      {
        orgid: string
        inspirationRef: InspirationRef
        customerName: string
      }
    >({
      query: ({ orgid, inspirationRef, customerName }) => ({
        url: `/api/organizations/${orgid}/inspirations/${inspirationRef}/customer_copy`,
        method: "POST",
        data: { customer_name: customerName },
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "OrgInspirations", id: orgid },
      ],
    }),

    convertCopyToGuide: builder.mutation<
      { message: string },
      { orgid: string; inspirationRef: InspirationRef }
    >({
      query: ({ orgid, inspirationRef }) => ({
        url: `/api/organizations/${orgid}/inspirations/${inspirationRef}/convert_to_guide`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "OrgInspirations", id: orgid },
      ],
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch {
          return
        }
        dispatch(inspirationApi.util.invalidateTags(["Inspirations"]))
      },
    }),

    updateInspirationTags: builder.mutation<
      unknown,
      {
        orgid: string
        inspirationRef: InspirationRef
        tagIds: string[]
        // bulk fan-outs invalidate once at the end instead of per request
        skipInvalidation?: boolean
      }
    >({
      query: ({ inspirationRef, tagIds }) => ({
        url: `/api/inspirations/${inspirationRef}`,
        method: "PUT",
        data: { where: "tags", tags: tagIds },
      }),
      invalidatesTags: (_result, _error, { orgid, skipInvalidation }) =>
        skipInvalidation ? [] : [{ type: "OrgInspirations", id: orgid }],
    }),

    deleteOrgInspiration: builder.mutation<
      InspirationSummary,
      {
        orgid: string
        inspirationRef: InspirationRef
        skipInvalidation?: boolean
      }
    >({
      query: ({ inspirationRef }) => ({
        url: `/api/inspirations/${inspirationRef}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { orgid, skipInvalidation }) =>
        skipInvalidation ? [] : [{ type: "OrgInspirations", id: orgid }],
      async onQueryStarted({ skipInvalidation }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch {
          return
        }
        if (!skipInvalidation) {
          dispatch(inspirationApi.util.invalidateTags(["Inspirations"]))
        }
      },
    }),

    duplicateOrgInspiration: builder.mutation<
      InspirationSummary,
      { orgid: string; inspirationRef: InspirationRef }
    >({
      query: ({ inspirationRef }) => ({
        url: `/api/inspirations/duplicate`,
        method: "POST",
        data: { inspiration_ref: inspirationRef },
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "OrgInspirations", id: orgid },
      ],
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch {
          return
        }
        dispatch(inspirationApi.util.invalidateTags(["Inspirations"]))
      },
    }),

    createOrgInspirationTag: builder.mutation<
      InspirationTagDefinition,
      { orgid: string; name: string; color: InspirationTagColor }
    >({
      query: ({ orgid, name, color }) => ({
        url: `/api/organizations/${orgid}/inspiration_tags`,
        method: "POST",
        data: { name, color },
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "OrgInspirations", id: orgid },
      ],
    }),

    deleteOrgInspirationTag: builder.mutation<
      { message: string },
      { orgid: string; tagId: string }
    >({
      query: ({ orgid, tagId }) => ({
        url: `/api/organizations/${orgid}/inspiration_tags/${tagId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "OrgInspirations", id: orgid },
      ],
    }),

    reorderOrgInspirations: builder.mutation<
      { message: string },
      { orgid: string; inspirationIds: InspirationRef[] }
    >({
      query: ({ orgid, inspirationIds }) => ({
        url: `/api/organizations/${orgid}/inspirations/order`,
        method: "PUT",
        data: { inspiration_ids: inspirationIds },
      }),
      invalidatesTags: (_result, _error, { orgid }) => [
        { type: "OrgInspirations", id: orgid },
      ],
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch {
          return
        }
        dispatch(inspirationApi.util.invalidateTags(["Inspirations"]))
      },
    }),

    getOrgVanityReports: builder.query<VanityReports, string>({
      query: (orgid) => ({
        url: `/api/vanity/report/organization`,
        method: "POST",
        data: { orgid, dateRange: "all" },
      }),
      providesTags: (_result, _error, orgid) => [
        { type: "OrgVanityReports", id: orgid },
      ],
    }),

    getOrgCreatorInfo: builder.query<{ handle: string; email: string }, string>(
      {
        query: (orgid) => ({
          url: `/api/organizations/${orgid}/creator`,
        }),
      }
    ),
  }),
})

export const {
  useGetOrganizationQuery,
  useUpdateOrganizationConfigMutation,
  useGetMicrositeQuery,
  useSetupMicrositeMutation,
  useGetAvailableFeaturesQuery,
  useGetPermissionsForOrgQuery,
  useGetUserCountriesQuery,
  useGetOrgChatsQuery,
  useGetOrgChatInsightsQuery,
  useGetOrgChatInsightsTrendsQuery,
  useGetOrgCtaInsightsQuery,
  useGetOrgChatFilterOptionsQuery,
  useGetOrgPlacesQuery,
  useUpdateFeaturedPlaceMutation,
  useRemoveFeaturedPlaceMutation,
  useGetOrgPlacesStatusQuery,
  useGetOrgUsersQuery,
  useGetOrgFeedbacksQuery,
  useUpdateOrgFeedbackMutation,
  useAnalyzeOrgFeedbackMutation,
  useLazyGetOrgChatMessagesQuery,
  useGetOrgCrawlsQuery,
  useTriggerOrgCrawlMutation,
  useGetOrgCrawledUrlsQuery,
  useGetOrgMembersQuery,
  useSendOrgInvitationMutation,
  useChangeOrgMemberRolesMutation,
  useRemoveOrgMemberMutation,
  useGetOrgKeyFactsQuery,
  useCreateOrgKeyFactMutation,
  useUpdateOrgKeyFactMutation,
  useDeleteOrgKeyFactMutation,
  useGetOrgInspirationsQuery,
  useUpdateInspirationTagsMutation,
  useDeleteOrgInspirationMutation,
  useDuplicateOrgInspirationMutation,
  useCreateOrgInspirationTagMutation,
  useDeleteOrgInspirationTagMutation,
  useReorderOrgInspirationsMutation,
  useCreateCustomerCopyMutation,
  useConvertCopyToGuideMutation,
  useGetOrgVanityReportsQuery,
  useGetOrgCreatorInfoQuery,
} = organizationApi

// check if any request in flight
export function selectOrgPlacesFetching(state: RootState, orgid: string) {
  return organizationApi.util
    .selectCachedArgsForQuery(state, "getOrgPlaces")
    .some(
      (args) =>
        args.orgid === orgid &&
        organizationApi.endpoints.getOrgPlaces.select(args)(state).status ===
          QueryStatus.pending
    )
}

export function selectOrgUsersFetching(state: RootState, orgid: string) {
  return organizationApi.util
    .selectCachedArgsForQuery(state, "getOrgUsers")
    .some(
      (args) =>
        args.orgid === orgid &&
        organizationApi.endpoints.getOrgUsers.select(args)(state).status ===
          QueryStatus.pending
    )
}

export function selectOrgCrawledUrlsFetching(state: RootState, orgid: string) {
  return organizationApi.util
    .selectCachedArgsForQuery(state, "getOrgCrawledUrls")
    .some(
      (args) =>
        args.orgid === orgid &&
        organizationApi.endpoints.getOrgCrawledUrls.select(args)(state)
          .status === QueryStatus.pending
    )
}

export function populateOrgCache(
  dispatch: AppDispatch,
  organization: OrganizationAdmin,
  orgPermissions?: PerOrganizationPermission[]
) {
  void dispatch(
    organizationApi.util.upsertQueryData(
      "getOrganization",
      organization.orgid,
      organization
    )
  )
  if (orgPermissions) {
    void dispatch(
      organizationApi.util.upsertQueryData(
        "getPermissionsForOrg",
        organization.orgid,
        orgPermissions
      )
    )
  }
}
